import { useState } from 'react';
import PropTypes from 'prop-types';
import { Download } from 'lucide-react';
import SkillRadar from './SkillRadar';
import SkillBars from './SkillBars';
import DecisionDots from './DecisionDots';
import { BandLegend, bandOf } from './microSimBands';

/**
 * One person's micro simulation report, visual first: tiles, radar, bars, decision
 * dots. Long text (notes, the workplace action) sits below the fold or behind a tap.
 *
 * Shown to the participant for their OWN run (D3: no partner overlay), and to staff
 * opening one person from the group view — same component, same data shape
 * (backend microSimSerializer.forReport).
 */

const ME_COLOR = '#6366F1';

const MicroSimReport = ({ report, onDownloadPdf = null, compact = false }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const label = (key) => report.skills.find((s) => s.key === key)?.label;
  const strongest = label(report.strongest);
  const focus = label(report.practiceFocus);
  const values = Object.fromEntries(report.skills.map((s) => [s.key, s.score]));

  const download = async () => {
    setDownloading(true);
    setDownloadError('');
    try {
      await onDownloadPdf();
    } catch (err) {
      setDownloadError(err?.userMessage || err?.message || 'Could not download the PDF.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={compact ? 'w-full' : 'mx-auto w-full max-w-xl px-4 pb-8 pt-4'}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wider text-primary-600">
            {compact ? 'Practice results' : 'Your practice results'}
          </p>
          <h1 className={`${compact ? 'text-lg' : 'text-2xl'} mt-0.5 font-bold text-neutral-900`}>{report.topic.title}</h1>
          <p className="mt-0.5 truncate text-sm text-neutral-500">
            {report.me.name}{report.partnerName ? ` · practised with ${report.partnerName}` : ''}
          </p>
        </div>
        {onDownloadPdf && (
          <button
            type="button"
            onClick={download}
            disabled={downloading}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-800 hover:bg-neutral-50 disabled:opacity-60"
          >
            <Download className="h-4 w-4" />
            {downloading ? 'Preparing…' : 'PDF'}
          </button>
        )}
      </div>
      {downloadError && <p className="mt-2 text-xs text-red-600">{downloadError}</p>}

      <div className="mt-3 grid grid-cols-2 gap-2.5">
        <div className={`rounded-2xl px-3.5 py-3 ${bandOf(80).soft}`}>
          <p className={`text-[11px] font-semibold ${bandOf(80).text}`}>Strongest skill</p>
          <p className="mt-0.5 font-bold leading-tight text-neutral-900">{strongest || '—'}</p>
        </div>
        <div className={`rounded-2xl px-3.5 py-3 ${focus ? bandOf(0).soft : bandOf(80).soft}`}>
          <p className={`text-[11px] font-semibold ${focus ? bandOf(0).text : bandOf(80).text}`}>Practice focus</p>
          <p className="mt-0.5 font-bold leading-tight text-neutral-900">{focus || 'Nothing below 100%'}</p>
        </div>
      </div>

      <div className="mt-3 rounded-2xl border border-neutral-200 bg-white p-3">
        <SkillRadar skills={report.skills} series={[{ name: report.me.name || 'You', color: ME_COLOR, values }]} height={compact ? 240 : 250} />
      </div>

      <div className="mt-3 rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-sm font-bold text-neutral-900">Skills</h2>
          <BandLegend />
        </div>
        <div className="mt-3">
          <SkillBars skills={report.skills} showDescriptions={!compact} />
        </div>
      </div>

      <div className="mt-3 rounded-2xl border border-neutral-200 bg-white p-4">
        <h2 className="mb-2 text-sm font-bold text-neutral-900">{compact ? 'Decisions' : 'Your decisions'}</h2>
        <DecisionDots decisions={report.decisions} outcomes={report.outcomes} />
      </div>

      {report.workplaceAction && (
        <div className="mt-3 rounded-2xl bg-primary-50 p-4 ring-1 ring-inset ring-primary-200">
          <p className="text-[11px] font-bold uppercase tracking-wide text-primary-700">Try this at work</p>
          <p className="mt-1 text-sm text-neutral-900">{report.workplaceAction}</p>
        </div>
      )}

      <p className="mt-4 text-xs leading-relaxed text-neutral-400">
        {report.disclaimer || 'This is learning feedback, not a validated assessment.'}
      </p>
    </div>
  );
};

MicroSimReport.propTypes = {
  report: PropTypes.object.isRequired,
  /** Returns a promise; shows a PDF button when given. */
  onDownloadPdf: PropTypes.func,
  /** Tighter layout for use inside a modal or a details tab. */
  compact: PropTypes.bool,
};

export default MicroSimReport;
